import React from 'react'
import { RouteComponentProps } from 'react-router-dom'

import styled from 'styled-components'

import { style } from 'theme'

const AppLink = styled.button`
  text-decoration: underline;
  color: ${style((theme) => theme.colors.slate)};
  display: inline-block;
  border: none;
  background: inherit;
  cursor: pointer;
  padding: 0;
`

const webviewExit = (path: string) => {
  window.location.assign(path)
}

type HelpText = {
  (
    isWebView: boolean,
    history: RouteComponentProps<{}>['history'],
    handleShowArticle: (id: string) => void,
  ): JSX.Element
}

const HelpTextMap: { [helpTextKey: string]: HelpText } = {
  acorns_costs_too_much: (isWebView, history, handleShowArticle) => (
    <>
      We're sorry to hear. Did you know you can offset our monthly subscription
      through{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=found-money')
            : history.push('/earn/found-money', { referrer: location.pathname })
        }
      >
        bonus investments
      </AppLink>{' '}
      and{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=refer-friends')
            : history.push('/invite', { referrer: location.pathname })
        }
      >
        friend referrals
      </AppLink>
      ? Before you go,{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('acdf89cd-5267-45e8-9d26-90824a0a97e2')
        }
      >
        check out all of the ways
      </AppLink>{' '}
      to grow we've packed into our system.
    </>
  ),
  cant_afford_to_invest_at_this_time: (_, __, handleShowArticle) => (
    <>
      We know setting aside money can be easier at some times than others, but
      waiting to invest can cost you even more. Even if it's just spare change,
      the sooner you invest, the more time it has to grow.
      <br />
      Before you go, learn more{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('d3f7b053-b367-462a-a04d-8f0389abac7d')
        }
      >
        here
      </AppLink>
      .
    </>
  ),
  concerned_about_the_market: (isWebView, history, _) => (
    <>
      Historically, the market has grown significantly over time. But if you
      feel anxious, you may benefit from a more conservative portfolio. View our
      conservative portfolio{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=portfolio')
            : history.push('/invest/core/portfolio', {
                referrer: location.pathname,
              })
        }
      >
        here
      </AppLink>
      .
    </>
  ),
  dont_want_to_invest: (_, __, handleShowArticle) => (
    <>
      Before you go, did you know investing gives you a chance to build wealth
      on top of your paycheck through compounding? That's when returns on your
      investments can earn returns on their own, so your money can grow
      exponentially over time! Learn more{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('7c39764c-f53c-4458-8c0a-c55823f89a7c')
        }
      >
        here
      </AppLink>
      .
    </>
  ),

  not_enough_control_over_my_investments: (isWebView, history, _) => (
    <>
      Did you know you can change your portfolio any time, without a fee? Our
      five expertly-constructed portfolios range from conservative (all bonds)
      to aggressive (all stocks). Each contains a mix of exchange-traded funds
      (ETFs), which provide exposure to more than 7,000 stocks and bonds.
      <br />
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=portfolio')
            : history.push('/invest/core/portfolio', {
                referrer: location.pathname,
              })
        }
      >
        Explore our portfolios here.
      </AppLink>
    </>
  ),

  not_sure_how_acorns_works: (_, __, handleShowArticle) => (
    <>
      We’re sorry it wasn’t more clear. Check out{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('acdf89cd-5267-45e8-9d26-90824a0a97e2')
        }
      >
        this article
      </AppLink>{' '}
      to learn how you can make the most out of your Acorns account.
    </>
  ),
  not_the_right_time_to_invest: (isWebView, history, _) => (
    <>
      Historically, the market has grown significantly over time. But if you
      feel anxious during market dips, you may benefit from a more conservative
      portfolio. View our conservative portfolio{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=portfolio')
            : history.push('/invest/core/portfolio', {
                referrer: location.pathname,
              })
        }
      >
        here
      </AppLink>
      .
    </>
  ),
  other: () => (
    <>
      We’re sorry to see you go. Please let us know what we could have done
      better.
    </>
  ),
  poor_investment_performance: (_, __, handleShowArticle) => (
    <>
      We know it's hard to see a loss, but if you stop investing now, your money
      won't have a chance to recover and grow. The most successful investors
      stick with it because historically, every downturn has ended in an upturn.
      Before you lock in losses, learn more about it{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('b655957d-a6bd-4e87-a411-feb9fb7afc32')
        }
      >
        here
      </AppLink>
      .
    </>
  ),
  switching_to_another_service: (_, __, handleShowArticle) => (
    <>
      We’re sorry to hear. Maybe we just haven’t done a good enough job of
      showing how to get the most out of Acorns. Check out{' '}
      <AppLink
        onClick={() =>
          handleShowArticle('9b783244-5132-452a-8bce-90de0eab1024')
        }
      >
        this article
      </AppLink>{' '}
      to learn all the ways we help your money grow. If you still decide to
      leave, you can always come back. Simply log back in and reopen your
      account.
    </>
  ),
  this_is_temporary_ill_be_back: () => (
    <>
      We're sorry to see you go, but we'll be here to help you grow when you're
      ready! Simply log back in and reopen your account.
    </>
  ),
  trouble_connecting_my_bank: (isWebView, history, _) => (
    <>
      Did you know you can manually link your account with your routing and
      account number and set up a recurring investment.{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=link_account')
            : history.push('/settings/linked-accounts', {
                referrer: location.pathname,
              })
        }
      >
        Click here
      </AppLink>{' '}
      to get started.
    </>
  ),
  unexpected_charges_to_my_bank: (isWebView, history, _) => (
    <>
      We’re sorry to hear this. We transfer money from your bank based on your
      investment settings. You can change those{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? webviewExit('acorns://exit?navigate_to=invest')
            : history.push('/invest/core', {
                referrer: location.pathname,
              })
        }
      >
        here
      </AppLink>
      . The monthly fee is also transferred from your linked bank account and
      you can view your billing date{' '}
      <AppLink
        onClick={() =>
          isWebView
            ? history.push('/wv/my-subscription')
            : history.push('/settings/my-subscription', {
                referrer: location.pathname,
              })
        }
      >
        here
      </AppLink>
      .
    </>
  ),
}

export default HelpTextMap
